import { lazy } from "react";

import type { TenantModule } from "../../domain/tenantModule";
import { kccFeeds } from "./feeds";
import { kccPriorityDisplay } from "./priority";
import { RunSalesOutboundTab } from "./slots/RunSalesOutboundTab";

/**
 * The KCC customer module. Mounted only when `boot.modules[]` names `kcc`.
 *
 * All four screens are real. `slots` fills the one KCC use — the run-detail tab — while
 * `slots.siteDetail` stays undefined (the extension table marks it unused by KCC), and
 * `registerExtensions` and `findingRenderers` stay undefined: KCC extends no core register,
 * and the finding renderer is group 9's. The Home card (`slots.home`) is gone: Home no longer
 * has a module slot to fill (see `src/shell/ModuleSlot.tsx`).
 *
 * `display.priority` is UD-9: core stores and edits priority on the internal scale (99 = most
 * important) and KCC's people read the SAP scale (0 = most important), so the module supplies the
 * adapter and core's registers, tier tables and policy form ask for it. See `./priority`.
 *
 * `feeds` is N3: KCC's own delivery kinds (`sales_order`, `sales_forecast`, `sto_output`) for the
 * feed log's kind filter, and its two sales datasets for the data catalogue. See `./feeds`.
 */
export const kcc: TenantModule = {
  key: "kcc",
  name: "KCC sales planning",
  navigation: {},
  routes: [
    {
      path: "/kcc/sales-planning",
      title: "Sales planning",
      desktop: lazy(() => import("./pages/desktop/SalesPlanning")),
      tablet: lazy(() => import("./pages/tablet/SalesPlanning")),
      permission: "kcc-sales-workspace:read",
      module: "kcc",
    },
    {
      path: "/kcc/sales-orders",
      title: "Sales orders",
      desktop: lazy(() => import("./pages/desktop/SalesOrders")),
      permission: "kcc-sales-workspace:read",
      module: "kcc",
    },
    {
      path: "/kcc/sales-forecasts",
      title: "Sales forecasts",
      desktop: lazy(() => import("./pages/desktop/SalesForecasts")),
      permission: "kcc-sales-workspace:read",
      module: "kcc",
    },
    {
      path: "/kcc/overrides",
      title: "Overrides",
      desktop: lazy(() => import("./pages/desktop/Overrides")),
      permission: "kcc-sales-workspace:read",
      module: "kcc",
    },
  ],
  slots: {
    planningRunDetail: RunSalesOutboundTab,
  },
  feeds: kccFeeds,
  display: {
    priority: kccPriorityDisplay,
  },
  labels: {
    site: "Location",
    sites: "Locations",
    siteWorkspace: "Location workspace",
    product: "SKU",
    products: "SKUs",
    transferRequest: "STR",
    transferRequests: "STRs",
    shipment: "STO",
    shipments: "STOs",
  },
};

export default kcc;
